import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import UseLoginHook from "hooks/UseLoginHook";

const AuthenticationForm = () => {
  const navigate = useNavigate();

  // ✅ Steps: login → forgot → otp → reset
  const [step, setStep] = useState("login");
  const [method, setMethod] = useState("mobile"); // 'mobile' or 'email'
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const {
    loginLoader,
    loginUser,
    forgotPassword,
    resetPassword,
  } = UseLoginHook();

  // Timer for resend OTP
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  // ✅ Handle login
  const handleLogin = async () => {
    setError("");
    if (method === "mobile" && !mobile.trim())
      return setError("Enter mobile number");
    if (method === "email" && !email.trim())
      return setError("Enter email address");
    if (!password.trim()) return setError("Enter password");

    const payload =
      method === "mobile" ? { mobile, password } : { email, password };
    const success = await loginUser(payload);
    // if (success) navigate("/dashboard");
  };

  // ✅ Handle Forgot Password (Send OTP)
  const handleSendOtp = async () => {
    setError("");
    if (method === "mobile" && !mobile.trim())
      return setError("Enter mobile number");
    if (method === "email" && !email.trim())
      return setError("Enter email address");

    const payload = method === "mobile" ? { mobile } : { email };
    const success = await forgotPassword(payload);
    if (success) {
      setSuccess(`OTP sent to your ${method}`);
      setStep("reset");
      setResendTimer(60);
    }
  };

  // ✅ Handle Reset Password
  const handleResetPassword = async () => {
    setError("");
    if (!otp.trim()) return setError("Enter OTP");
    if (!newPassword.trim()) return setError("Enter new password");
    if (newPassword !== confirmPassword)
      return setError("Passwords do not match");

    const payload =
      method === "mobile"
        ? { mobile, otp, newPassword }
        : { email, otp, newPassword };

    const success = await resetPassword(payload);
    if (success) {
      setSuccess("Password reset successful! Please login.");
      setStep("login");
      setEmail("");
      setMobile("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // ✅ Resend OTP
  const handleResend = () => {
    if (resendTimer > 0) return;
    handleSendOtp();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#E3403B] rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {step === "login"
              ? "Welcome to Workly"
              : step === "forgot"
              ? "Forgot Password"
              : "Reset Password"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {step === "login"
              ? "Login using email or mobile with password"
              : step === "forgot"
              ? "Enter your email or mobile to receive OTP"
              : "Enter the OTP and set your new password"}
          </p>
        </div>

        {/* ---------------- LOGIN ---------------- */}
        {step === "login" && (
          <>
            <div className="flex justify-center mb-6">
              <Button
                variant={method === "mobile" ? "default" : "ghost"}
                className={`mx-1 ${
                  method === "mobile" ? "bg-[#E3403B] text-white" : ""
                }`}
                onClick={() => setMethod("mobile")}
              >
                Mobile
              </Button>
              <Button
                variant={method === "email" ? "default" : "ghost"}
                className={`mx-1 ${
                  method === "email" ? "bg-[#E3403B] text-white" : ""
                }`}
                onClick={() => setMethod("email")}
              >
                Email
              </Button>
            </div>

            {method === "mobile" ? (
              <Input
                label="Mobile Number"
                type="tel"
                placeholder="Enter mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            ) : (
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            <Input
              label="Password"
              type="password"
              placeholder="Enter password"
              value={password}
              className="mt-4"
              required
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && (
              <p className="text-destructive text-sm flex items-center mt-2">
                <Icon name="AlertCircle" size={16} className="mr-1" /> {error}
              </p>
            )}

            <Button
              fullWidth
              onClick={handleLogin}
              loading={loginLoader}
              iconName="LogIn"
              className="mt-6 text-white"
            >
              Login
            </Button>

            <div className="text-center mt-4">
              <button
                className="text-sm text-[#E3403B] hover:underline"
                onClick={() => setStep("forgot")}
              >
                Forgot Password?
              </button>
            </div>
          </>
        )}

        {/* ---------------- FORGOT PASSWORD ---------------- */}
        {step === "forgot" && (
          <>
            <div className="flex justify-center mb-6">
              <Button
                variant={method === "mobile" ? "default" : "ghost"}
                className={`mx-1 ${
                  method === "mobile" ? "bg-[#E3403B] text-white" : ""
                }`}
                onClick={() => setMethod("mobile")}
              >
                Mobile
              </Button>
              <Button
                variant={method === "email" ? "default" : "ghost"}
                className={`mx-1 ${
                  method === "email" ? "bg-[#E3403B] text-white" : ""
                }`}
                onClick={() => setMethod("email")}
              >
                Email
              </Button>
            </div>

            {method === "mobile" ? (
              <Input
                label="Mobile Number"
                type="tel"
                placeholder="Enter your mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            ) : (
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            {error && <p className="text-destructive text-sm mt-2">{error}</p>}
            {success && <p className="text-success text-sm mt-2">{success}</p>}

            <Button
              fullWidth
              onClick={handleSendOtp}
              loading={loginLoader}
              iconName="Send"
              className="mt-6 text-white"
            >
              Send OTP
            </Button>

            <Button
              variant="ghost"
              fullWidth
              onClick={() => setStep("login")}
              className="mt-2"
            >
              Back to Login
            </Button>
          </>
        )}

        {/* ---------------- RESET PASSWORD ---------------- */}
        {step === "reset" && (
          <>
            <Input
              label="OTP"
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
            />

            <Input
              label="New Password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-4"
            />

            <Input
              label="Confirm Password"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-4"
            />

            {error && <p className="text-destructive text-sm mt-2">{error}</p>}
            {success && <p className="text-success text-sm mt-2">{success}</p>}

            <Button
              fullWidth
              onClick={handleResetPassword}
              loading={loginLoader}
              iconName="CheckCircle"
              className="mt-6 text-white"
            >
              Reset Password
            </Button>

            <Button
              variant="ghost"
              fullWidth
              onClick={() => setStep("forgot")}
              disabled={resendTimer > 0}
              className="mt-2"
            >
              {resendTimer > 0
                ? `Resend OTP in ${resendTimer}s`
                : "Resend OTP"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthenticationForm;
